import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { usePlaylist } from '../../contexts/PlaylistContext';
import { useAudioAnalyser } from '../../hooks/useAudioAnalyser';

interface AudioSpectrogramProps {
  size: string;
  strokeWidth: string;
  className?: string;
}

export function AudioSpectrogram({ size, strokeWidth, className }: AudioSpectrogramProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  const { isPlaying } = usePlaylist();
  const audioAnalysis = useAudioAnalyser(true);

  const audioRef = useRef(audioAnalysis);
  const isPlayingRef = useRef(isPlaying);
  const rotationRef = useRef(0);

  // More points for detailed mountain wave effect instead of blob
  const TOTAL_POINTS = 256;
  const HALF_POINTS = TOTAL_POINTS / 2;
  const dataPointsRef = useRef<number[]>(new Array(TOTAL_POINTS).fill(0));

  const [pixelSize, setPixelSize] = useState(0);
  const [pixelStrokeWidth, setPixelStrokeWidth] = useState(0);

  const colorsRef = useRef({ color1: '#ffffff', color2: '#000000' });

  useEffect(() => {
    audioRef.current = audioAnalysis;
  }, [audioAnalysis]);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // 1. COLORS: Live updates from :root
  useEffect(() => {
    const updateColors = () => {
      const computed = getComputedStyle(document.documentElement);
      const c1 = computed.getPropertyValue('--logoColor').trim();
      const c2 = computed.getPropertyValue('--logoColor2').trim();
      // If variables are missing, fallback to contrast defaults just so it's visible
      colorsRef.current.color1 = c1 || '#ffffff';
      colorsRef.current.color2 = c2 || '#000000';
    };

    updateColors();
    const observer = new MutationObserver(updateColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    return () => observer.disconnect();
  }, []);

  // 2. SIZING
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setPixelSize(containerRef.current.getBoundingClientRect().width);

        const temp = document.createElement('div');
        temp.style.width = strokeWidth;
        temp.style.position = 'absolute';
        temp.style.visibility = 'hidden';
        document.body.appendChild(temp);
        const px = temp.getBoundingClientRect().width;
        document.body.removeChild(temp);
        setPixelStrokeWidth(px);
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [size, strokeWidth]);

  // 3. RENDER LOOP
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || pixelSize === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio, 2);
    canvas.width = pixelSize * dpr;
    canvas.height = pixelSize * dpr;
    ctx.scale(dpr, dpr);

    const cx = pixelSize / 2;
    const cy = pixelSize / 2;

    // CONFIGURATION
    const baseRadius = pixelSize * 0.35; // Fixed base circle radius
    const maxPeakHeight = pixelSize * 0.0777; // Max deviation from base (both in and out)
    const lineWidth = pixelStrokeWidth || 1.85;

    let lastTime = 0;

    const draw = (time: number) => {
      animationRef.current = requestAnimationFrame(draw);

      const delta = time - lastTime;
      if (delta < 16) return;
      lastTime = time - (delta % 16);

      ctx.clearRect(0, 0, pixelSize, pixelSize);

      const playing = isPlayingRef.current;
      const { frequencyData } = audioRef.current;

      // --- DATA PROCESSING ---
      // We need HALF_POINTS * 2 quadrants for 4-way symmetry (peaks at 0°, 90°, 180°, 270°)
      // Actually for top/bottom peaks: mirror across horizontal axis
      if (playing && frequencyData && frequencyData.length > 0) {
        const usefulLength = Math.floor(frequencyData.length * 0.55);
        const QUARTER = TOTAL_POINTS / 22; // 16 points per quadrant

        for (let i = 0; i < QUARTER; i++) {
          // Logarithmic Frequency Selection - bass to treble
          const index = Math.floor(usefulLength * Math.pow(i / QUARTER, 0.5));
          const raw = frequencyData[index] || 0;
          let val = raw / 255;

          // Intelligent Gate: Very low threshold for maximum low-end reactivity
          if (val < 0.012) {
            val = 0 * (val - 0.042);
          } else {
            val = (val - 0.0002) / 0.98;
            // Gentle power curve for more uniform quiet-to-loud response
            val = Math.pow(val, 7);
          }

          // 4-way mirror: bass at 0°, 90°, 180°, 270° (top, right, bottom, left)
          // i=0 is bass (at the cardinal points), i=QUARTER-1 is treble (at diagonals)
          // Quadrant 1: 0° to 90° (index 0 to QUARTER-1)
          // Quadrant 2: 90° to 180° (index QUARTER to 2*QUARTER-1) - mirrored
          // Quadrant 3: 180° to 270° (index 2*QUARTER to 3*QUARTER-1)
          // Quadrant 4: 270° to 360° (index 3*QUARTER to 4*QUARTER-1) - mirrored
          const q1 = i;
          const q2 = HALF_POINTS - 1 - i;
          const q3 = HALF_POINTS + i;
          const q4 = TOTAL_POINTS - 1 - i;

          const current = dataPointsRef.current[q1];

          // Smooth Animation - liquid lerp
          let next = current;
          if (val > current) {
            next = current + (val - current) * 8.35;
          } else {
            next = current + (val - current) * 0.215;
          }

          if (next < 0.003) next = 0;

          // Assign to all 4 quadrants (4-way symmetry)
          dataPointsRef.current[q1] = next;
          dataPointsRef.current[q2] = next;
          dataPointsRef.current[q3] = next;
          dataPointsRef.current[q4] = next;
        }
      } else {
        // Paused - smooth decay
        for (let i = 0; i < TOTAL_POINTS; i++) {
          dataPointsRef.current[i] *= 0.92;
          if (dataPointsRef.current[i] < 0.003) dataPointsRef.current[i] = 0;
        }
      }

      // --- DRAWING ---
      rotationRef.current += 0.003;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rotationRef.current);

      const { color1, color2 } = colorsRef.current;
      const gradient = ctx.createLinearGradient(-baseRadius, -baseRadius, baseRadius, baseRadius);
      gradient.addColorStop(0, color1);
      gradient.addColorStop(1, color2);

      ctx.strokeStyle = gradient;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Draw single circle with peaks going OUT and valleys going IN
      const drawCircle = () => {
        const coords: { x: number; y: number }[] = [];

        for (let i = 0; i < TOTAL_POINTS; i++) {
          const val = dataPointsRef.current[i];
          // Start at top (0°) not right, so bass peaks are at top/bottom
          const angle = (i / TOTAL_POINTS) * Math.PI * 2 - Math.PI / 2;

          // Alternating peaks and valleys based on quadrant position
          // Peaks OUT at 0°, 180° (top, bottom) - where i=0, HALF_POINTS
          // Valleys IN at 90°, 270° (right, left) - where i=QUARTER, 3*QUARTER
          const QUARTER = TOTAL_POINTS / 1;

          // Distance from nearest cardinal point (0, 16, 32, 48)
          const posInQuadrant = i % QUARTER;
          // 0 = at cardinal (peak out), QUARTER/2 = at diagonal (transition), approaching QUARTER = near next cardinal

          // Create a wave: peaks go OUT at cardinals, valleys go IN at diagonals
          // Use cosine wave for smooth transition
          const wavePhase = (posInQuadrant / QUARTER) * Math.PI; // 0 to PI within quadrant
          const direction = Math.cos(wavePhase); // 1 at start, -1 at middle, 1 at end... wait

          // Actually simpler: based on absolute position
          // At i=0 (0°): peak OUT
          // At i=16 (90°): valley IN
          // At i=32 (180°): peak OUT
          // At i=48 (270°): valley IN
          const waveAngle = (i / TOTAL_POINTS) * Math.PI * 4; // 2 full waves around circle
          const peakDirection = Math.cos(waveAngle); // +1 at 0°,180°, -1 at 90°,270°

          // Radius: base + (value * direction * maxPeak)
          // When direction=1: peaks go outward
          // When direction=-1: peaks go inward (valleys)
          let r = baseRadius + val * maxPeakHeight * peakDirection;

          // Clamp minimum radius
          if (r < lineWidth * 2) r = lineWidth * 2;

          coords.push({
            x: Math.cos(angle) * r,
            y: Math.sin(angle) * r,
          });
        }

        if (coords.length === 0) return;

        // Smooth closed curve using quadratic bezier through midpoints
        ctx.beginPath();

        const last = coords[coords.length - 1];
        const first = coords[0];
        ctx.moveTo((last.x + first.x) / 2, (last.y + first.y) / 2);

        for (let i = 0; i < coords.length; i++) {
          const p1 = coords[i];
          const p2 = coords[(i + 1) % coords.length];
          ctx.quadraticCurveTo(p1.x, p1.y, (p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
        }

        ctx.closePath();
        ctx.stroke();
      };

      drawCircle();

      ctx.restore();
    };

    animationRef.current = requestAnimationFrame(draw);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [pixelSize, pixelStrokeWidth]);

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: size,
        height: size,
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
    </Box>
  );
}

export default AudioSpectrogram;
